import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from "recharts";

import { AgentCompatibilityRadarScores } from "@/api/generated";
import theme from "@/theme";

import { FeaturedCompatibilityRadar } from "./FeaturedCompatibilityRadar";

export type RadarScoreKey = keyof AgentCompatibilityRadarScores;

// ユーザー単独の5軸スコアは存在しないため、グレー塗りの系列は全軸3固定の「相性基準ライン」（参照枠）とする。
// 青線がグレーをはみ出す（=3以上）軸ほど相性がよい、という読み方になる。
const BASELINE_SCORE_LINE = 3;

export const RADAR_SCORE_LABELS: { key: RadarScoreKey; label: string }[] = [
  { key: "property_type", label: "物件種別" },
  { key: "annual_income", label: "年収" },
  { key: "financial_asset", label: "資産" },
  { key: "investment_stage", label: "ステージ" },
  { key: "area", label: "エリア" },
];

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
  },
  chart: {
    height: 260,
  },
  compactChart: {
    height: 168,
  },
  legend: {
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(2),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  legendSwatch: {
    width: 15,
    height: 15,
    borderRadius: 2,
  },
  yourSwatch: {
    backgroundColor: theme.palette.black[300],
  },
  theirSwatch: {
    border: `1.5px solid ${theme.palette.primary.main}`,
    backgroundColor: "transparent",
  },
  legendLabel: {
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: "0.05em",
    color: theme.palette.text.primary,
  },
  introduction: {
    "& $chart": { width: 260, maxWidth: "100%", height: 200, margin: "0 auto" },
    "& $compactChart": { width: 176, maxWidth: "100%", height: 140, margin: "0 auto" },
    "& $legend": { marginTop: 16, gap: 20, flexWrap: "wrap" },
    "& $legendSwatch": { width: 16, height: 16, borderRadius: 4, flexShrink: 0 },
    "& $yourSwatch": { backgroundColor: theme.palette.semantic["chart/baseline"] },
    "& $legendLabel": { fontWeight: 400, letterSpacing: 0 },
  },
  planner: {
    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 40,
      "& $chart": { margin: 0, width: 240, height: 200 },
      "& $legend": { flexDirection: "column", marginTop: 0, gap: 12 },
      "& $legendLabel": { fontSize: 14 },
    },
    [theme.breakpoints.between("md", "md")]: {
      flexDirection: "column",
      gap: 16,
      "& $legend": {flexDirection:"row",flexWrap:"wrap",justifyContent:"center"},
    },
  },
  introductionCompact: { "& $legend": { marginTop: 8 } },
}));

export const hasCompatibilityRadarScores = (scores?: AgentCompatibilityRadarScores | null): boolean => {
  if (!scores) return false;
  return RADAR_SCORE_LABELS.some(({ key }) => typeof scores[key] === "number");
};

interface CompatibilityRadarChartProps {
  // このプランナーとの軸別相性スコア（実データ or イントロ説明用のサンプル値）
  scores?: AgentCompatibilityRadarScores | null;
  size?: "full" | "compact";
  hideLegend?: boolean;
  variant?: "legacy" | "introduction" | "planner" | "featured";
}

export const CompatibilityRadarChart: React.FC<CompatibilityRadarChartProps> = ({
  scores,
  size = "full",
  hideLegend = false,
  variant = "legacy",
}) => {
  const classes = useStyles();
  const isCompact = size === "compact";
  const isIntroduction = variant === "introduction" || variant === "planner";

  const chartData = React.useMemo(() => {
    return RADAR_SCORE_LABELS.map(({ key, label }) => ({
      label,
      yours: BASELINE_SCORE_LINE,
      theirs: scores?.[key] ?? 0,
    }));
  }, [scores]);

  if (!hasCompatibilityRadarScores(scores)) {
    return null;
  }

  if (variant === "featured") {
    return <FeaturedCompatibilityRadar scores={scores} labels={RADAR_SCORE_LABELS} hideLegend={hideLegend} />;
  }

  return (
    <Box
      className={`${variant === "planner" ? classes.planner : ""} ${classes.root} ${isIntroduction ? classes.introduction : ""} ${isIntroduction && isCompact ? classes.introductionCompact : ""}`}
    >
      <Box className={isCompact ? classes.compactChart : classes.chart}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart
            data={chartData}
            outerRadius={isIntroduction ? (isCompact ? "66%" : "74%") : isCompact ? "60%" : "72%"}
          >
            <PolarGrid stroke={theme.palette.semantic["border/default"]} />
            <PolarAngleAxis
              dataKey="label"
              tick={{
                fontSize: isCompact ? 10 : 12,
                ...(isIntroduction ? { fill: theme.palette.semantic["text/secondary"] } : {}),
              }}
            />
            <PolarRadiusAxis angle={90} domain={[0, 5]} tickCount={6} tick={false} axisLine={false} />
            <Radar
              name="相性基準ライン"
              dataKey="yours"
              stroke={theme.palette.black[300]}
              fill={theme.palette.black[300]}
              fillOpacity={isIntroduction ? 1 : 0.6}
              isAnimationActive={false}
            />
            <Radar
              className="planner-compatibility-series"
              name="このプランナーとの相性"
              dataKey="theirs"
              stroke={theme.palette.primary.main}
              fill="transparent"
              strokeWidth={2}
              isAnimationActive={false}
            />
          </RadarChart>
        </ResponsiveContainer>
      </Box>
      {!hideLegend && (
        <Box className={classes.legend}>
          <Box className={classes.legendItem}>
            <Box className={`${classes.legendSwatch} ${classes.yourSwatch}`} />
            <Typography className={classes.legendLabel}>相性基準ライン</Typography>
          </Box>
          <Box className={classes.legendItem}>
            <Box className={`${classes.legendSwatch} ${classes.theirSwatch}`} />
            <Typography className={classes.legendLabel}>このプランナーとの相性</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
